export function computeDamage(rawAtk, defenderDef) {
    return Math.max(1, Math.floor(rawAtk - defenderDef));
}
export function enemySide(side) { return side === 'A' ? 'B' : 'A'; }
export function getRuntimes(state, side) {
    return side === 'A' ? state.sideA : state.sideB;
}
export function livingEnemies(state, attackerSide) {
    const enemies = getRuntimes(state, enemySide(attackerSide));
    return enemies
        .map((runtime, slot) => ({ slot, runtime }))
        .filter(e => e.runtime.alive);
}
export function livingAllies(state, side) {
    return getRuntimes(state, side)
        .map((runtime, slot) => ({ slot, runtime }))
        .filter(e => e.runtime.alive);
}
export function lowestHpEnemy(state, attackerSide) {
    const list = livingEnemies(state, attackerSide);
    if (list.length === 0)
        return null;
    return list.reduce((min, e) => (e.runtime.hp < min.runtime.hp ? e : min));
}
export function effectiveAtk(r) {
    let pct = 0;
    for (const s of r.statuses) {
        if (s.kind === 'buff') {
            pct += (s.params.atk ?? 0);
            pct += (s.params.synergyAtkPct ?? 0);
        }
        else if (s.kind === 'debuff') {
            pct -= (s.params.atk ?? 0);
        }
    }
    return Math.max(1, r.atk * (1 + pct));
}
export function effectiveDef(r) {
    let pct = 0;
    for (const s of r.statuses) {
        if (s.kind === 'buff')
            pct += (s.params.def ?? 0);
        else if (s.kind === 'debuff')
            pct -= (s.params.def ?? 0);
    }
    return Math.max(0, r.def * (1 + pct));
}
export function applyDamage(state, source, target, amount, isCrit, rng) {
    const targetRuntime = getRuntimes(state, target.side)[target.slot];
    if (!targetRuntime.alive)
        return 0;
    // Dodge roll first — if dodged, zero damage, no shield consumption, no event.
    if (rng) {
        const dodge = targetRuntime.statuses.find(s => s.kind === 'dodge_chance');
        if (dodge && rng.chance(dodge.params.chance ?? 0)) {
            state.events.push({
                t: state.tick, kind: 'status_apply',
                side: target.side, slot: target.slot,
                status: 'dodged', duration: 0
            });
            return 0;
        }
    }
    // Mark bonus — incoming damage scales by sum of mark.bonusPct on target.
    let modAmount = amount;
    let markBonus = 0;
    for (const s of targetRuntime.statuses) {
        if (s.kind === 'mark')
            markBonus += (s.params.bonusPct ?? 0);
    }
    if (markBonus > 0)
        modAmount = Math.floor(modAmount * (1 + markBonus));
    // Shield consumption (preserved from prior fix)
    let remaining = modAmount;
    for (const s of targetRuntime.statuses) {
        if (remaining <= 0)
            break;
        if (s.kind === 'shield') {
            const absorb = Math.min(remaining, s.params.amount ?? 0);
            s.params.amount = (s.params.amount ?? 0) - absorb;
            remaining -= absorb;
        }
    }
    targetRuntime.statuses = targetRuntime.statuses.filter(s => !(s.kind === 'shield' && (s.params.amount ?? 0) <= 0));
    if (remaining <= 0)
        return 0;
    // Apply HP damage and emit events
    const dealt = Math.min(remaining, targetRuntime.hp);
    targetRuntime.hp -= dealt;
    state.events.push({
        t: state.tick, kind: 'damage',
        side: target.side, slot: target.slot,
        amount: dealt, isCrit
    });
    if (targetRuntime.hp <= 0) {
        targetRuntime.hp = 0;
        targetRuntime.alive = false;
        state.events.push({ t: state.tick, kind: 'ko', side: target.side, slot: target.slot });
    }
    return dealt;
}
export function applyHeal(state, target, amount) {
    const r = getRuntimes(state, target.side)[target.slot];
    if (!r.alive)
        return;
    const healed = Math.min(amount, r.hpMax - r.hp);
    r.hp += healed;
    state.events.push({ t: state.tick, kind: 'heal', side: target.side, slot: target.slot, amount: healed });
}
export function hasStatus(r, kind) {
    return r.statuses.some(s => s.kind === kind);
}
